import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Btn, Card, Modal, Input, Select, Empty, Spinner, Badge, useToast } from '../components/shared/UI';
import { useT } from '../i18n';
import api from '../utils/api';

export const CASE_STATUS_COLORS = {
  open: '#F59E0B', in_progress: '#2E75B6', waiting_on_user: '#8B5CF6',
  resolved: '#10B981', closed: '#94a3b8',
};
export const CASE_PRIORITY_COLORS = { low: '#94a3b8', normal: '#2E75B6', high: '#EF4444' };
export const CASE_CATEGORIES = ['billing', 'bug', 'question', 'feature', 'other'];

const MAX_ATTACHMENTS = 3;

export function useAttachments(show, t) {
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef(null);
  const add = e => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 2 * 1024 * 1024) { show(t('support.attachTooLarge'), 'error'); return; }
      const reader = new FileReader();
      reader.onloadend = () =>
        setAttachments(a => a.length >= MAX_ATTACHMENTS ? a : [...a, reader.result]);
      reader.readAsDataURL(file);
    });
    if (fileRef.current) fileRef.current.value = '';
  };
  const remove = i => setAttachments(a => a.filter((_, idx) => idx !== i));
  const clear = () => setAttachments([]);
  return { attachments, fileRef, add, remove, clear };
}

export function AttachmentPicker({ att, t }) {
  return (
    <div>
      <input ref={att.fileRef} type="file" accept="image/*" multiple onChange={att.add} style={{ fontSize: 12 }} />
      {att.attachments.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {att.attachments.map((a, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={a} alt="" style={{ height: 56, borderRadius: 6, border: '1px solid var(--border)' }} />
              <button type="button" onClick={() => att.remove(i)} title={t('common.delete')} style={{
                position: 'absolute', top: -6, insetInlineEnd: -6, width: 18, height: 18,
                borderRadius: '50%', background: 'var(--danger)', color: '#fff', border: 'none',
                fontSize: 11, lineHeight: 1, cursor: 'pointer',
              }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Support() {
  const navigate = useNavigate();
  const { t } = useT();
  const { show, ToastContainer } = useToast();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'question', priority: 'normal', message: '' });
  const [saving, setSaving] = useState(false);
  const att = useAttachments(show, t);

  useEffect(() => {
    api.get('/support').then(r => setCases(r.data)).finally(() => setLoading(false));
  }, []);

  const openNew = () => {
    setForm({ subject: '', category: 'question', priority: 'normal', message: '' });
    att.clear();
    setModal(true);
  };

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post('/support', { ...form, attachments: att.attachments });
      show(t('support.created', { n: data.caseNumber }));
      setModal(false);
      navigate(`/support/${data._id}`);
    } catch (err) {
      show(err.response?.data?.message || t('support.createFailed'), 'error');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <ToastContainer />
      <PageHeader title={t('support.title')} subtitle={t('support.subtitle')}
        actions={<Btn variant="primary" onClick={openNew}>+ {t('support.newCase')}</Btn>} />

      {loading ? <Spinner /> : cases.length === 0
        ? <Empty icon="🎧" message={t('support.empty')}
            action={<Btn variant="primary" onClick={openNew}>{t('support.newCase')}</Btn>} />
        : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {cases.map((c, i) => (
              <button key={c._id} onClick={() => navigate(`/support/${c._id}`)} style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'start',
                padding: '14px 18px', background: i % 2 ? 'var(--hover-bg)' : 'transparent',
                border: 'none', borderBottom: '1px solid var(--border-light)', cursor: 'pointer',
                fontFamily: 'inherit', color: 'var(--text)',
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.subject}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                    {c.caseNumber} · {t(`support.cat.${c.category}`)} · {new Date(c.lastActivityAt).toLocaleString()}
                  </div>
                </div>
                {c.status === 'waiting_on_user' && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8B5CF6' }}>{t('support.yourTurn')}</span>
                )}
                <Badge label={t(`support.status.${c.status}`)} color={CASE_STATUS_COLORS[c.status]} />
              </button>
            ))}
          </Card>
        )}

      <Modal open={modal} onClose={() => setModal(false)} title={t('support.newCase')} width={560}>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label={t('support.subject')} value={form.subject} required maxLength={200}
            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            placeholder={t('support.subjectPh')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label={t('support.category')} value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {CASE_CATEGORIES.map(c => <option key={c} value={c}>{t(`support.cat.${c}`)}</option>)}
            </Select>
            <Select label={t('support.priority')} value={form.priority}
              onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
              {['low', 'normal', 'high'].map(p => <option key={p} value={p}>{t(`support.prio.${p}`)}</option>)}
            </Select>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>{t('support.message')}</div>
            <textarea value={form.message} required rows={5}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder={t('support.messagePh')}
              style={{
                width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                border: '1px solid var(--border)', borderRadius: 8, resize: 'vertical',
                background: 'var(--input-bg)', color: 'var(--text)', outline: 'none',
              }} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
              {t('support.attachments')}
            </div>
            <AttachmentPicker att={att} t={t} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" type="button" onClick={() => setModal(false)}>{t('common.cancel')}</Btn>
            <Btn variant="primary" type="submit" disabled={saving}>
              {saving ? t('common.saving') : t('support.submit')}
            </Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
